/**
 * Floored ticker meta for Wiseguy symbol chrome (earnings E + 52W).
 * Fetches GET /v1/steamroom/tickers/meta — trust server phase, do not re-window.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fznService,
  type SteamroomTickerMeta,
} from '../services/fznService';

const META_STALE_MS = 45_000;
const META_SYMBOL_CAP = 500;

export interface UseWiseguyTickerMetaResult {
  /** Uppercased symbol → meta. Empty while loading / on error. */
  bySymbol: ReadonlyMap<string, SteamroomTickerMeta>;
  isLoading: boolean;
  /** ET session date used for the request. Empty until the board is dated. */
  date: string;
}

/**
 * @param symbols - Symbols currently on the board (any case). Deduped + capped.
 * @param date - Board ET day YYYY-MM-DD from QF `session_date`. Do not pass
 *   calendar today — `/v1/steamroom/tickers/meta` requires a date, and inventing
 *   today dates chrome a session later than the tape.
 */
export function useWiseguyTickerMeta(
  symbols: readonly string[],
  date?: string
): UseWiseguyTickerMetaResult {
  const boardDate = date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';

  const uniqueSorted = useMemo(() => {
    const set = new Set<string>();
    for (const s of symbols) {
      const u = s.trim().toUpperCase();
      if (u) set.add(u);
    }
    return [...set].sort().slice(0, META_SYMBOL_CAP);
  }, [symbols]);

  const key = uniqueSorted.join(',');

  const { data, isLoading } = useQuery({
    queryKey: ['steamroomTickerMeta', boardDate, key],
    queryFn: () => fznService.getTickersMeta(boardDate, uniqueSorted),
    staleTime: META_STALE_MS,
    refetchInterval: META_STALE_MS,
    enabled: uniqueSorted.length > 0 && !!boardDate,
    placeholderData: (prev) => prev,
  });

  const bySymbol = useMemo(() => {
    const map = new Map<string, SteamroomTickerMeta>();
    if (!data?.tickers) return map;
    for (const [sym, m] of Object.entries(data.tickers)) {
      map.set(sym.toUpperCase(), m);
    }
    return map;
  }, [data]);

  return { bySymbol, isLoading, date: boardDate };
}
